// Shared utility functions for deployment-related functionality

import {getFirestore} from "firebase/firestore";
import { Deployment, Rollout, MainCommit, PullRequestDetails, isDeploymentOutOfDate } from '@finvari/deliver';

export const getDeploymentStatusIcon = (status: string) => {
  switch (status) {
    case 'success':
      return '✅';
    case 'failed':
      return '❌';
    case 'in_progress':
      return '⏳';
    default:
      return '❓';
  }
};

export const getDeploymentStatusColor = (status: string) => {
  switch (status) {
    case 'success':
      return 'status-success';
    case 'failed':
      return 'status-error';
    case 'in_progress':
      return 'status-warning';
    default:
      return 'status-unknown';
  }
};

export const getTestStatusIcon = (status: string) => {
  switch (status) {
    case 'passing':
      return '✅';
    case 'failing':
      return '❌';
    case 'pending':
      return '⏳';
    default:
      return '❓';
  }
};

export const getTestStatusColor = (status: string) => {
  switch (status) {
    case 'passing':
      return 'status-success';
    case 'failing':
      return 'status-error';
    case 'pending':
      return 'status-warning';
    default:
      return 'status-unknown';
  }
};

export const getDuration = (startTime: string, endTime?: string) => {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : new Date();
  const duration = end.getTime() - start.getTime();

  const minutes = Math.floor(duration / 60000);
  const seconds = Math.floor((duration % 60000) / 1000);

  return `${minutes}m ${seconds}s`;
};

export const formatDate = (dateString: string | undefined) => {
  if (!dateString) {
    return 'Unknown date';
  }
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Invalid date';
  }
};

export const formatDateTime = (dateString: string | undefined) => {
  if (!dateString) {
    return 'Unknown date';
  }
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    return 'Invalid date';
  }
};

export const getCloudBuildUrl = (buildId: string, environment: string): string => {
  const myProject = getFirestore().app.options.projectId;
  return `https://console.cloud.google.com/cloud-build/builds;region=us-central1/${buildId}?project=${myProject}`;
};

// Determine if any deployments in an environment are out of date
export const getEnvironmentOutOfDateStatus = (
  moduleDeployments: Map<string, Deployment>,
  pullRequests: PullRequestDetails[],
  mainCommits: MainCommit[]
): { isOutOfDate: boolean; outOfDateCount: number; totalCount: number } => {
  if (moduleDeployments.size === 0) {
    return { isOutOfDate: false, outOfDateCount: 0, totalCount: 0 };
  }

  let outOfDateCount = 0;
  const totalCount = moduleDeployments.size;

  moduleDeployments.forEach((deployment, module) => {
    if (!deployment.commitHash) {
      return;
    }

    let isOutOfDate = false;

    if (deployment.prNumber && deployment.prNumber > 0) {
      // This is a PR deployment - compare with PR's latest commit
      const pr = pullRequests.find(p => p.pr.number === deployment.prNumber);
      if (pr && pr.lastCommit) {
        isOutOfDate = isDeploymentOutOfDate(deployment, pr.lastCommit);
      }
    } else {
      // This is a main branch deployment - compare with latest main commit
      const latestMainCommit = mainCommits.find(commit => commit.order === 0);
      if (latestMainCommit) {
        isOutOfDate = isDeploymentOutOfDate(deployment, {
          hash: latestMainCommit.hash,
          date: latestMainCommit.date
        });
      }
    }

    if (isOutOfDate) {
      outOfDateCount++;
    }
  });

  return {
    isOutOfDate: outOfDateCount > 0,
    outOfDateCount,
    totalCount
  };
};

// Check if a rollout is out of date compared to the latest commit
export const isRolloutOutOfDate = (
  rollout: Rollout,
  latestCommitHash: string | undefined
): boolean => {
  if (!rollout.commitHash || !latestCommitHash) {
    return false;
  }
  return rollout.commitHash !== latestCommitHash;
};
